import { IModel, IAttribute } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Constants, type ITridentResource, NamedAsyncAdapter, ReferenceAdapter, Tag } from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import {
    containsParameter,
    containsExpressionObject,
    isLogicAppExpression,
    containsLogicAppExpression
} from 'app/shared/utils/expression.util';
import { merge } from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { referAKVinNonCredentialProperties } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { Parameter } from 'app/model/base/parameter';
import { getString, stringIsNullOrEmpty, isString, convertObjectToString } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { IConnectionContext } from 'app/interfaces/copilot.pipeline.action.types';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { type LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { driverVersions } from 'app/registries/consts/tabular-consts';

export abstract class LinkedServiceAdapter<
    T extends LinkedService,
    U extends Model.LinkedServiceResource = Model.LinkedServiceResource,
    D extends IConnectionContext = IConnectionContext
> extends NamedAsyncAdapter<T, U, D> {
    public getArtifactId(resource: U): string {
        return resource?.properties.typeProperties?.artifactId;
    }

    public getWorkspaceObjectId(resource: U): string {
        return resource?.properties.typeProperties?.workspaceId;
    }

    public parseConnectionString?(
        connectionString: string,
        linkedService: T,
        additionalFieldsParameterizable: boolean
    ): void {
        this._parseConnectionString(connectionString, linkedService, additionalFieldsParameterizable);
    }

    public override async toFabricCopilotContext(linkedService: T): Promise<D> {
        try {
            const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            );
            const lsModelDef = Metamodel.modelOf(linkedService);
            const lsConfigutation = linkedServiceRegistryService.getLinkedServiceConfiguration(lsModelDef.name);
            if (linkedService) {
                const result = {
                    Name: linkedService.name,
                    Type: lsConfigutation.isKnownInPipelineContext ? lsModelDef.name : 'Unknown',
                    OriginalConnectionType: lsConfigutation.isKnownInPipelineContext ? undefined : lsModelDef.name
                };
                return result as D;
            }
            return undefined;
        } catch (e) {
            this._logWarning(`Failed to convert linkedService to Fabric context: ${convertObjectToString(e)}`);
            return undefined;
        }
    }

    public override async fromFabricCopilotContext(context: D, linkedService?: T): Promise<T> {
        if (!linkedService) {
            throw new Error(
                'LinkedService should be initialized before calling LinkedService::fromFabricCopilotContext'
            );
        }
        return linkedService;
    }

    protected _parseConnectionString(
        connectionString: string,
        linkedService: T,
        additionalFieldsParameterizable: boolean
    ): void {
        return;
    }

    /**
     * If the adapter is not used to handle the properties, please set this value to true
     * Shouldn't be used for first class experience
     * Currently, only GenericLinkedServiceAdapter and LinkedServiceDynamicLoaderService are using it
     */
    protected skipProperties = false;

    /**
     * If all the things (expect shared parts) in property in the linked service is related to credential, please set this value to false in the specific adapter
     *   Shared parts means: description, connectVia, annotations, parameters
     * It's closely related to LinkedService.encryptedCredential and LinkedService.cachedResource
     * Current assumption is that all the things in the property is related to credential, so
     *   1. Currently, no adapter is setting it to false
     *   2. Currently, the logic for false is not implemented, will only know how to handle it after we meet the first exception
     */
    protected allPropertiesRelatedToCredential = true;

    /**
     * If the linked service type supports first class parameter experience, set the value to true
     * Also related to the supportFirstClassParameterExperience in the metadata, please change both of them at the same time
     */
    protected supportFirstClassParameterExperience = false;

    // translates Trident payload to ADF payload, so linked service model in Fabric is also filled.
    protected toAzureTypeProperties?(tridentResource: ITridentResource): Model.LinkedService['typeProperties'];

    protected override async _toAzureResource(tridentResource: ITridentResource): Promise<U> {
        const artifactType = tridentResource.artifactType;
        const name = tridentResource.datasourceName ?? tridentResource.displayName;
        const azureResource: Model.LinkedServiceResource & Record<'objectId' | 'workspaceObjectId', string> = {
            name,
            id: `/subscriptions/xxxxxxxxx/resourceGroups/xxxx/providers/Microsoft.DataFactory/factories/xxxxxxx/linkedservices/${name}`,
            objectId: tridentResource.objectId,
            workspaceObjectId: tridentResource.workspaceObjectId,
            properties: {
                ...(this.toAzureTypeProperties ? {} : tridentResource),
                type: this.model().name,
                tridentArtifactType: tridentResource.artifactType
            }
        };
        if (tridentResource.credentialDetails) {
            azureResource.properties.credentialType = tridentResource.credentialDetails.credentialType;
        }
        try {
            azureResource.properties.typeProperties = this.toAzureTypeProperties?.(tridentResource);
        } catch (e) {
            this._logError(`Failed to parse trident resource: ${e}`, {
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType
            });
        }
        try {
            if (artifactType === 'Connection') {
                const datasourceReference = JSON.parse(tridentResource.datasourceReference);
                azureResource.properties.tridentConnectionKind = datasourceReference.kind;
                azureResource.properties.fabricGatewayType = tridentResource.gatewayType;
                azureResource.properties.fabricGatewayName = tridentResource.clusterName;
                azureResource.properties.tridentConnectionAuthKind = tridentResource.credentialDetails?.credentialType;
                azureResource.properties.allowDatasourceThroughOnPremsGateway =
                    tridentResource.allowDatasourceThroughGateway;
                azureResource.properties.memberGateways = tridentResource.memberGateways;
            }
        } catch (e) {
            this._logError(`Failed to parse connection kind: ${e}`, {
                objectId: tridentResource.objectId,
                datasourceType: tridentResource.datasourceType
            });
        }

        if (tridentResource.artifactType === 'Connection') {
            // Entity GatewayClusterDatasource:
            // PowerQuery-Web\src\Product\Fabric\ConnectionCore\DmtsPowerBi\JsonContracts\GatewayClusterDatasource.cs
            azureResource.properties.rawTridentResource = tridentResource;
        }

        return azureResource as unknown as U;
    }

    protected async _toResource(linkedService: T): Promise<U> {
        let linkedServiceResource = (await super._toResource(linkedService)) as U;

        if (!this.skipProperties) {
            if (!this.allPropertiesRelatedToCredential) {
                throw new Error('Logic for allPropertiesRelatedToCredential = false not implemented');
            }
            // Trident linked services don't have encrypted credential, so we can skip the logic for encrypted credential
            if (
                !linkedService.isTridentLinkedService &&
                linkedService.encryptedCredential != null &&
                !this.context.removeEncryptedCredentials
            ) {
                // Use the cached resource
                // Following things may be changed if there is encrypted credential
                //   description (always not related to credential)
                //   connectVia (if change from cloud IR to cloud IR, not related to credential)
                //   annotations (always not related to credential)
                this.properties(linkedService.cachedResource);
                linkedService.cachedResource.properties.description = linkedServiceResource.properties
                    ? linkedServiceResource.properties.description
                    : undefined;
                linkedService.cachedResource.properties.connectVia =
                    linkedService.integrationRuntime?.name != null
                        ? (ReferenceAdapter.toResource(
                            linkedService.integrationRuntime,
                            this.context
                        ) as Model.IntegrationRuntimeReference)
                        : undefined;
                linkedService.cachedResource = this.annotationsToResource(
                    linkedService,
                    linkedService.cachedResource as U
                );
                return linkedService.getCachedResourceCopy() as U;
            }
            // parameters
            const parameters: { [propertyName: string]: Model.ParameterSpecification } = {};
            const parameterAdapter = this.context.adapterFor(Parameter.MODEL);
            if (parameterAdapter && linkedService.parameters != null && linkedService.parameters.size > 0) {
                linkedService.parameters.forEach((parameter) => {
                    if (parameter.name) {
                        parameters[getString(parameter.name)] = parameterAdapter.toResource(parameter);
                    }
                });
                this.properties(linkedServiceResource).parameters = parameters;
            }
            linkedServiceResource = this.annotationsToResource(linkedService, linkedServiceResource);
            if (!linkedService.fullyByJsonEditor) {
                // Ignore the properties and only use the things in raw if user choose to fully use the things in json editor
                linkedServiceResource = await this.propertiesToResourceAsync(linkedService, linkedServiceResource);
            } else {
                // If fullyByJsonEditor, still need to set the type to the name of the model
                linkedServiceResource.properties = {
                    type: this.model().name
                };
            }
        }

        // json editor
        if (linkedService.raw) {
            const parsed = JSON.parse(linkedService.raw);
            merge(linkedServiceResource, parsed);
        }

        if (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__) {
            Reflect.deleteProperty(linkedServiceResource.properties, 'tridentArtifactType');
            Reflect.deleteProperty(linkedServiceResource, 'workspaceObjectId');
        }

        linkedServiceResource = await this.connectVia(linkedService, linkedServiceResource);

        return linkedServiceResource;
    }

    protected async _toModel(linkedServiceResource: U, linkedService?: T): Promise<T> {
        // Create the model if the model doesn't exist
        if (!linkedService) {
            linkedService = new (this.modelConstructor())();
        }

        await super._toModel(linkedServiceResource, linkedService);

        // Clean parameters and tags, these elements will be populated again below.
        linkedService.parameters?.clear();
        linkedService.tags?.clear();
        linkedService.parameterized = false;
        linkedService.fullyByJsonEditor = false;

        // Shared parts
        if (!this.skipProperties) {
            linkedService.setCachedResource(
                linkedServiceResource.properties != null && linkedServiceResource.properties.typeProperties != null
                    ? linkedServiceResource.properties.typeProperties.connectionProperties != null
                        ? linkedServiceResource.properties.typeProperties.connectionProperties.encryptedCredential
                        : linkedServiceResource.properties.typeProperties.connectionProperties
                    : undefined,
                linkedServiceResource
            );

            if (linkedServiceResource.properties && linkedServiceResource.properties.connectVia != null) {
                linkedService.integrationRuntime = ReferenceAdapter.toProxyModel<IntegrationRuntime>(
                    Metamodel.modelFor(IntegrationRuntime),
                    linkedServiceResource.properties.connectVia,
                    this.context
                );
            }

            // if some none-credential property use akv, UI won't handle it, so put all the things into raw
            const spec = this.context.adapterSpecFor(this.model());
            if (await referAKVinNonCredentialProperties(linkedServiceResource, spec, this.context)) {
                linkedService.fullyByJsonEditor = true;
                this._logWarning('LinkedServiceAdapter-ReferAKVinNonCredentialProperties', {
                    LinkedServiceName: linkedService.name,
                    LinkedServiceType: linkedServiceResource.properties
                        ? linkedServiceResource.properties.type
                        : undefined
                });
            }

            // If the linked service uses expression, and it doesn't support first class parameter experience, there will be no need to use parse the properties, simply put all the things into raw
            if (containsParameter(linkedServiceResource) || containsExpressionObject(linkedServiceResource)) {
                linkedService.parameterized = true;
                if (!this.supportFirstClassParameterExperience) {
                    this._logWarning('LinkedServiceAdapter-ReferParameterInUnSupportFirstClassExperience', {
                        LinkedServiceName: linkedService.name,
                        LinkedServiceType: linkedServiceResource.properties
                            ? linkedServiceResource.properties.type
                            : undefined
                    });
                }
                // should also take existing linkedService.fullyByJsonEditor into consideration
                // if supportFirstClassParameterExperience is true, we'll check whether linkedService.fullyByJsonEditor has been updated in line 135
                // this works when a linked service supports FirstClassParameterExperience while references AKV in NonCredential Properties
                linkedService.fullyByJsonEditor =
                    !this.supportFirstClassParameterExperience || linkedService.fullyByJsonEditor;
            }
            if (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__) {
                // In Trident, we define parameters in the pipeline. So we need some special handling here.
                linkedService.parameterized =
                    containsLogicAppExpression(linkedServiceResource) || linkedService.parameterized;
                linkedService.tridentArtifactType =
                    linkedService.tridentArtifactType ?? linkedServiceResource.properties?.tridentArtifactType;
                linkedService.tridentConnectionKind = linkedServiceResource.properties?.tridentConnectionKind;
                linkedService.tridentConnectionAuthKind = linkedServiceResource.properties?.tridentConnectionAuthKind;
                linkedService.fabricGatewayType = linkedServiceResource.properties?.fabricGatewayType;
                linkedService.fabricGatewayName = linkedServiceResource.properties?.fabricGatewayName;
                linkedService.allowDatasourceThroughOnPremsGateway =
                    linkedServiceResource.properties?.allowDatasourceThroughOnPremsGateway;
                linkedService.memberGateways = linkedServiceResource.properties?.memberGateways;
                if (linkedServiceResource.properties?.credentialType) {
                    linkedService.extension._credentialType$ = linkedServiceResource.properties.credentialType;
                }
            }
            linkedService.fullyByJsonEditor =
                linkedService.fullyByJsonEditor ||
                (linkedService.isExternalConnection && linkedServiceResource.properties?.typeProperties == null) ||
                linkedService.isSampleDatasetConnection ||
                this.fullyByJsonEditorCustomizer(linkedServiceResource);
            // Parameters
            const parameterAdapter = this.context.adapterFor(Parameter.MODEL);
            if (parameterAdapter) {
                if (linkedServiceResource.properties?.parameters) {
                    for (const parameterName in linkedServiceResource.properties.parameters) {
                        linkedService.parameters.add(
                            parameterAdapter.toModel(
                                linkedServiceResource.properties.parameters[parameterName],
                                undefined,
                                parameterName,
                                LinkedService.MODEL.name,
                                linkedService.name
                            )
                        );

                        if (
                            !GlobalVariables.strictParamValidation &&
                            !Constants.alphaNumericUnderscoreValidation.test(parameterName)
                        ) {
                            this._logWarning(
                                `Linked service "${linkedService.name}" with invalid parameter "${parameterName}".`
                            );
                        }
                    }
                }
            }
            // Annotations
            if (linkedServiceResource.properties?.annotations) {
                const tagAdapter = this.context.adapterFor(Tag.MODEL);
                for (const tagNamed of linkedServiceResource.properties.annotations) {
                    if (typeof tagNamed === 'string') {
                        linkedService.tags.add(tagAdapter.toModel(tagNamed));
                    } else {
                        linkedService.malformedTags.add(tagAdapter.toModel(tagNamed));
                    }
                }
            }

            if (!linkedService.fullyByJsonEditor && linkedServiceResource.properties?.typeProperties) {
                /**
                 * UI doesn't support 'FirstClassParameterExperience' in some fields, but customers could do it himself.
                 * In that case we will treat it as raw json.
                 */
                const typeProperties = linkedServiceResource.properties.typeProperties;
                linkedService.fullyByJsonEditor = Metamodel.modelOf(linkedService).attributes.some(
                    (attribute) => !attribute.templatable && isLogicAppExpression(typeProperties[attribute.name])
                );

                // special handling for connectionPropertiesInString
                // https://supportability.visualstudio.com/AzureDataFactory/_wiki/wikis/AzureDataFactory/2052814/Use-AKV-to-hold-connection-properties-for-new-V2-connectors
                if (typeProperties.connectionPropertiesInString) {
                    linkedService.fullyByJsonEditor = true;
                }

                /**
                 * handle DB2 `connectionString`
                 * `connectionString` is just like "server=@{linkedService().server};database=@{linkedService().database};username=@{linkedService().userName};Password=password;authenticationType=Basic"
                 * TODO: When `fullyByJsonEditorCustomizer` is ready, switch to it
                 */
                if (
                    Metamodel.modelOf(linkedService).name === adfModelTypeMap.db2.linkedServiceType &&
                    !linkedService.fullyByJsonEditor
                ) {
                    const connectionString: string = linkedServiceResource.properties.typeProperties.connectionString;
                    if (
                        isLogicAppExpression(connectionString) &&
                        connectionString.trim().startsWith('@') &&
                        !connectionString.trim().startsWith('@@')
                    ) {
                        linkedService.fullyByJsonEditor = true;
                    }
                }

                if (!linkedService.fullyByJsonEditor) {
                    // In some linked-services, frontend model's property name is not the same as the backend model's, so we must handle these properties manually.
                    const checkedList = [
                        { type: adfModelTypeMap.odata.linkedServiceType, keys: ['aadServicePrincipalCredentialType'] }
                    ];
                    for (const { type, keys } of checkedList) {
                        if (
                            Metamodel.modelOf(linkedService).name === type &&
                            keys.some((key) =>
                                isLogicAppExpression(linkedServiceResource.properties.typeProperties[key])
                            )
                        ) {
                            linkedService.fullyByJsonEditor = true;
                            break;
                        }
                    }
                }
            }

            linkedService.referencesWhenFullyByJsonEditor = [];
            if (linkedService.fullyByJsonEditor) {
                const propertiesInRaw = linkedServiceResource.properties
                    ? // eslint-disable-next-line prefer-object-spread
                    Object.assign({}, linkedServiceResource.properties)
                    : undefined;
                const resourceInRaw = {
                    properties: propertiesInRaw
                };
                linkedService.raw = JSON.stringify(resourceInRaw);
                this._calculateReferencedLinkedServicesFromRaw(linkedService);

                // version
                const lsWithVersion = ['Vertica', 'Teradata', 'Oracle', 'MySql', 'AmazonRdsForOracle', 'Greenplum', 'Presto', 'QuickBooks', 'Cassandra', 'Spark', 'Hubspot', 'Impala', 'AmazonRedshift', 'Jira', 'Xero', 'Netezza', 'Hive'];
                if (lsWithVersion.includes(linkedServiceResource.properties.type) && !__IS_FABRIC_DPE__) {
                    linkedService.isLegacyConnector = linkedServiceResource.properties.type === 'MySql' ?
                        linkedServiceResource.properties.typeProperties?.driverVersion !== 'v2' :
                        linkedServiceResource.properties.version !== '2.0';
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (linkedService as any).version = linkedService.isLegacyConnector ? driverVersions.v1 : driverVersions.v2;
                }
            } else {
                // In case the properties or typeProperties is empty
                linkedServiceResource.properties = (linkedServiceResource.properties || {}) as Model.LinkedService;
                linkedServiceResource.properties.typeProperties = linkedServiceResource.properties.typeProperties || {};
                linkedService = await this.propertiesToModelAsync(linkedServiceResource, linkedService);
                linkedService.raw = JSON.stringify(
                    await ValueAdapter.remainder(this.context, linkedServiceResource, this.model())
                );
            }
        }
        return linkedService;
    }

    /**
     * Constructor of the model, used in Resource -> Model
     */
    protected abstract modelConstructor(): new () => T;

    /**
     * Model of the linked service, used to generate the default .properties.type in Model -> Resource
     */
    protected abstract model(): IModel;

    /**
     * Main logic for linked service type specified Model -> Resource.
     * Handle almost all the things except encrypted credential in the properties part.
     * Will NOT be called if the encryptedCredential is already there. Will use the cached resource.
     */
    protected abstract propertiesToResourceAsync(linkedService: T, linkedServiceResource: U): Promise<U>;

    /**
     * Main logic for linked service type specified Resource -> Model. Handle almost all the things except encrypted credential in the properties part
     */
    protected abstract propertiesToModelAsync(linkedServiceResource: U, linkedService: T): Promise<T>;

    /**
     * Calculate if a specific linked service resource should be marked as fullyByJsonEditor in toModel().
     * By default, a linked service model will be fullyByJsonEditor with one of the following conditions met
     * 1. contains parameter while it does not support first-class ls parameter experience
     * 2. references AKV to a none 'secret' or 'akv' property
     * This func provides the extensibility for any customized fullyByJsonEditor setting logic
     */
    protected fullyByJsonEditorCustomizer(linkedServiceResource: U): boolean {
        return false;
    }
    /**
     * Used to generate .properties.connectVia and .properties.typeProperties.encryptedCredential in Model -> Resource
     */
    protected async connectVia(linkedService: LinkedService, resource: U): Promise<U> {
        if (linkedService.integrationRuntime != null && linkedService.integrationRuntime.name != null) {
            resource.properties.connectVia = ReferenceAdapter.toResource(
                linkedService.integrationRuntime,
                this.context
            ) as Model.IntegrationRuntimeReference;
        }
        if (!this.context.removeEncryptedCredentials) {
            if (linkedService.encryptedCredential) {
                // Use the original encryptedCredential
                const properties = this.properties(resource);
                if (properties.typeProperties == null) {
                    properties.typeProperties = {};
                }
                properties.typeProperties.encryptedCredential = linkedService.encryptedCredential;
            } else {
                const encryptedProperties = this.properties(resource);
                // encryptedCredential will have value only if it's on-prem ir, please don't relly on it
                if (!this.context.usedForQueryApi) {
                    linkedService.setCachedResource(
                        encryptedProperties != null && encryptedProperties.typeProperties != null
                            ? encryptedProperties.typeProperties.connectionProperties != null
                                ? encryptedProperties.typeProperties.connectionProperties.encryptedCredential
                                : encryptedProperties.typeProperties.encryptedCredential
                            : undefined,
                        resource
                    );
                }
            }
        }
        return resource;
    }

    protected _connectionStringToResource(connectionString: string): string | Model.SecureString {
        return !this.context.removeEncryptedCredentials
            ? connectionString
            : {
                type: 'SecureString',
                value: connectionString
            };
    }

    // Different from ValueAdapter.toResource, for linked service, the payload can't be { 'type': 'Expression', 'value': '@{linkedService().a}' }, it can only be '@{linkedService().a}'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected valueToResource(object: {}, attribute: IAttribute): any {
        const value = attribute.templateValue(object);
        return value !== undefined ? value : attribute.value(object);
    }

    protected annotationsToResource(linkedService: LinkedService, resource: U): U {
        const properties = this.properties(resource);
        properties.annotations = [];
        const tagAdapter = this.context.adapterFor(Tag.MODEL);
        for (const tagNamed of linkedService.tags) {
            // do not save it if user created an empty tag
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                properties.annotations.push(tagResource);
            }
        }
        for (const tagNamed of linkedService.malformedTags) {
            const tagResource = tagAdapter.toResource(tagNamed);
            if (tagResource) {
                properties.annotations.push(tagResource);
            }
        }
        return resource;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _calculateReferencedLinkedServicesFromRawInternal(linkedService: T, obj: any): void {
        Object.keys(obj).forEach((key) => {
            const subObj = obj[key];
            if (subObj != null) {
                const typeOf = typeof subObj;
                switch (typeOf) {
                    case 'object':
                        if (Array.isArray(subObj)) {
                            subObj.forEach((value) =>
                                this._calculateReferencedLinkedServicesFromRawInternal(linkedService, value)
                            );
                        } else if (subObj.type === 'LinkedServiceReference' && isString(subObj.referenceName)) {
                            linkedService.referencesWhenFullyByJsonEditor.push(
                                ReferenceAdapter.toProxyModel<LinkedService>(
                                    Metamodel.modelFor(LinkedService),
                                    subObj,
                                    this.context
                                )
                            );
                        } else {
                            this._calculateReferencedLinkedServicesFromRawInternal(linkedService, subObj);
                        }
                        break;
                    default:
                    // Do nothing
                }
            }
        });
    }

    private _calculateReferencedLinkedServicesFromRaw(linkedService: T): void {
        if (!stringIsNullOrEmpty(linkedService.raw)) {
            const obj = JSON.parse(linkedService.raw);
            this._calculateReferencedLinkedServicesFromRawInternal(linkedService, obj);
        }
    }
}
